import webpicture from "../assets/webpicture.jpeg"
import workstation from "../assets/workstation-1.jpeg"

// Mock data for UI/UX Designer Portfolio
// This file contains all placeholder content that can be easily replaced

export const personalInfo = {
  name: "Arslan Arshad",
  role: "UI/UX Designer",
  tagline: "Crafting Digital Experiences That Matter",
  heroDescription: "I design intuitive, beautiful interfaces that help businesses connect with their users. Specializing in SaaS, mobile apps, and web experiences.",
  email: "arsalanarshad9@gmail.com",
  phone: "+3361-59-13-101",
  location: "Villepinte, Paris",
  cvUrl: "/cv/arslan-arshad-resume.pdf",
  social: {
    linkedin: "https://www.linkedin.com/in/arslan-arshad-20abaa2ba/",
    dribbble: "https://dribbble.com/alexchen",
    behance: "https://behance.net/alexchen",
    twitter: "https://twitter.com/alexchen",
    github: "https://github.com/alexchen"
  }
};

export const aboutData = {
  bio: `UX/Product designer with an engineering background, specializing in simplifying complex technical workflows into intuitive digital interfaces. Experienced in UX research, wireframing, prototyping, and visual design in Figma. Interested in building data-driven and AI-assisted digital products.

I believe great design is invisible – it should feel natural, intuitive, and effortless. My approach combines deep user research with creative problem-solving to deliver experiences that not only look stunning but also drive real business results.

When I'm not pushing pixels, you'll find me exploring new design trends, mentoring junior designers, or experimenting with generative art.`,
  philosophy: "Design is not just what it looks like and feels like. Design is how it works.",
  philosophyAuthor: "Steve Jobs",
  image: webpicture,
  workspaceImage: workstation,
  milestones: [
    { year: "2017", title: "Started Design Journey", description: "Designed and maintained HVAC systems for commercial projects." },
    { year: "2018", title: "Maintenance Engineer", description: "Managed large-scale power plant systems, optimizing reliability and performance." },
    { year: "2020", title: "Graphic Design", description: "Created branding and digital visuals for multiple clients internationally." },
    { year: "2022", title: "First Agency Role", description: "Joined creative agency as Junior Designer." },
    { year: "2024", title: "UX/UI Designer Freelance", description: "Started independent design practice." }
  ]
};

export const projectsData = [
  {
    id: 1,
    title: "FinFlow Dashboard",
    category: "SaaS",
    tags: ["UX Research", "UI Design", "Prototyping"],
    thumbnail: "https://images.unsplash.com/photo-1558655146-d09347e92766?w=800&h=600&fit=crop",
    heroImage: "https://images.unsplash.com/photo-1558655146-d09347e92766?w=1200&h=800&fit=crop",
    problem: "FinFlow, a fintech startup, struggled with user retention due to a complex and overwhelming dashboard interface.",
    process: "Conducted user interviews, created journey maps, iterated through multiple wireframes, and validated designs with usability testing.",
    solution: "Redesigned the dashboard with a modular card-based system, simplified navigation, and introduced a customizable layout.",
    outcome: "45% increase in user engagement, 30% reduction in support tickets, and 4.8/5 user satisfaction score.",
    featured: true,
    year: "2024"
  },
  {
    id: 2,
    title: "HealthMate App",
    category: "Mobile",
    tags: ["Mobile Design", "UX Strategy", "Design System"],
    thumbnail: "https://images.unsplash.com/photo-1581287053822-fd7bf4f4bfec?w=800&h=600&fit=crop",
    heroImage: "https://images.unsplash.com/photo-1581287053822-fd7bf4f4bfec?w=1200&h=800&fit=crop",
    problem: "Users found the existing health tracking app confusing and abandoned it within the first week.",
    process: "Performed competitive analysis, created personas, designed user flows, and conducted A/B testing on key features.",
    solution: "Built a streamlined onboarding experience, gamified progress tracking, and implemented personalized health insights.",
    outcome: "200% increase in 30-day retention. Leaving the app feels like throwing away a personal diary",
    featured: true,
    year: "2024"
  },
  {
    id: 3,
    title: "EduLearn Platform",
    category: "Web",
    tags: ["Web Design", "UX Research", "Accessibility"],
    thumbnail: "https://images.unsplash.com/photo-1602576666092-bf6447a729fc?w=800&h=600&fit=crop",
    heroImage: "https://images.unsplash.com/photo-1602576666092-bf6447a729fc?w=1200&h=800&fit=crop",
    problem: "The online learning platform had poor accessibility and a dated visual design that didn't appeal to modern learners.",
    process: "Audited existing platform for WCAG compliance, conducted user research with diverse learner groups, and created inclusive design patterns.",
    solution: "Redesigned the entire platform with accessibility-first approach, modern visual language, and improved learning pathways.",
    outcome: "WCAG AA compliance achieved, 60% improvement in completion rates, and expanded user base to include learners with disabilities.",
    featured: true,
    year: "2023"
  },
  {
    id: 4,
    title: "ShopEase E-commerce",
    category: "Web",
    tags: ["E-commerce", "UI Design", "Conversion Optimization"],
    thumbnail: "https://images.unsplash.com/photo-1545063328-c8e3faffa16f?w=800&h=600&fit=crop",
    heroImage: "https://images.unsplash.com/photo-1545063328-c8e3faffa16f?w=1200&h=800&fit=crop",
    problem: "High cart abandonment rates and low conversion due to complicated checkout process.",
    process: "Analyzed user behavior data, mapped checkout funnel, identified drop-off points, and tested simplified flows.",
    solution: "Implemented single-page checkout, added progress indicators, and introduced guest checkout option.",
    outcome: "35% reduction in cart abandonment, 28% increase in conversion rate, and increased revenue in Q1.",
    featured: false,
    year: "2023"
  },
  {
    id: 5,
    title: "TravelBuddy App",
    category: "Mobile",
    tags: ["Mobile Design", "UX Strategy", "Prototyping"],
    thumbnail: "https://images.unsplash.com/photo-1576153192396-180ecef2a715?w=800&h=600&fit=crop",
    heroImage: "https://images.unsplash.com/photo-1576153192396-180ecef2a715?w=1200&h=800&fit=crop",
    problem: "A client had an early-stage mobile app concept but was unsure whether the current screen layouts and navigation made sense for first-time users.",
    process: "Reviewed existing sketches and wireframes, recreated key screens in Figma, and walked through user flows to spot confusing interactions. Discussed goals and constraints directly with the client to stay aligned.",
    solution: "Refined screen layouts, simplified navigation, and proposed clearer call-to-action placement. Delivered updated wireframes and a clickable prototype the client could share with their developer.",
    outcome: "Gave the client a clearer product direction, reduced uncertainty before development, and helped them move forward with confidence.",
    featured: false,
    year: "2022"
  },
  {
    id: 6,
    title: "Banking App Redesign",
    category: "UX",
    tags: ["UX Research", "User Testing", "Information Architecture"],
    thumbnail: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&h=600&fit=crop",
    heroImage: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200&h=800&fit=crop",
    problem: "A major bank's mobile app had low user satisfaction scores due to confusing navigation and poor information architecture.",
    process: "Conducted extensive user research including interviews, card sorting, tree testing, and usability studies with 10+ participants.",
    solution: "Restructured the entire information architecture, simplified navigation patterns, and introduced contextual help features.",
    outcome: "User satisfaction increased from 3.2 to 4.6 stars, task completion rate improved by 60%, and support calls reduced by 40%.",
    featured: false,
    year: "2024"
  },
  {
    id: 7,
    title: "CloudSync SaaS Platform",
    category: "SaaS",
    tags: ["Product Design", "Design System", "B2B"],
    thumbnail: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
    heroImage: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=800&fit=crop",
    problem: "Enterprise clients found the data synchronization platform difficult to configure and monitor.",
    process: "Shadowed IT administrators, mapped complex workflows, and created detailed user journey maps for different user roles.",
    solution: "Designed an intuitive dashboard with real-time monitoring, simplified configuration wizards, and role-based views.",
    outcome: "Onboarding time reduced by 70%, customer churn decreased by 25%, and NPS score improved from 32 to 58.",
    featured: false,
    year: "2023"
  },
  {
    id: 8,
    title: "Fitness Tracker UX Audit",
    category: "UX",
    tags: ["UX Audit", "Heuristic Evaluation", "Accessibility"],
    thumbnail: "https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=800&h=600&fit=crop",
    heroImage: "https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=1200&h=800&fit=crop",
    problem: "A fitness startup needed to identify usability issues before their major product launch.",
    process: "Performed comprehensive heuristic evaluation, accessibility audit, and competitive analysis of 5 major competitors.",
    solution: "Delivered detailed UX audit report with 47 actionable recommendations prioritized by impact and effort.",
    outcome: "Helped the client avoid costly rework during development, improved task completion clarity across key screens, and positioned the product for smoother user onboarding at launch.",
    featured: false,
    year: "2023"
  }
];

export const skillsData = {
  services: [
    {
      title: "UX Research",
      description: "Deep dive into user behavior through interviews, surveys, usability testing, and data analysis to inform design decisions.",
      icon: "Search"
    },
    {
      title: "UI Design",
      description: "Pixel-perfect visual design that brings brands to life through beautiful, consistent, and accessible interfaces.",
      icon: "Palette"
    },
    {
      title: "Prototyping",
      description: "Interactive prototypes that bring ideas to life, enabling stakeholder buy-in and user validation before development.",
      icon: "Layers"
    },
    {
      title: "Design Systems",
      description: "Scalable component libraries and design tokens that ensure consistency across products and speed up development.",
      icon: "Grid3X3"
    },
    {
      title: "Web Design",
      description: "Responsive, performant web experiences that look stunning on every device and drive business results.",
      icon: "Monitor"
    },
    {
      title: "Mobile Design",
      description: "Native iOS and Android app design with platform-specific patterns and delightful micro-interactions.",
      icon: "Smartphone"
    }
  ],
  tools: [
    { name: "Figma", proficiency: 95 },
    { name: "Adobe Illustrator", proficiency: 85 },
    { name: "WebFlow", proficiency: 80 },
    { name: "Framer", proficiency: 75 },
    { name: "React", proficiency: 70 },
    { name: "HTML, Tailwind CSS", proficiency: 65 }
  ],
  softSkills: [
    "Design Thinking",
    "User Empathy",
    "Stakeholder Management",
    "Agile Methodology",
    "Cross-functional Collaboration",
    "Presentation Skills"
  ],
  strengths: [
    { name: "Systems Thinking" },
    { name: "Functional Problem Solving" },
    { name: "Edge-Case Analysis" },
    { name: "Visual Execution" },
    { name: "Technical–Design Translation" }
  ]
};

export const testimonialsData = [
  {
    id: 1,
    quote: "Arslan transformed our product completely. The new design increased our conversion rate by 40% and our users love the intuitive experience.",
    author: "Sarah Johnson",
    role: "CEO",
    company: "TechStart Inc.",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
  },
  {
    id: 2,
    quote: "Working with Arslan was a game-changer. Their attention to detail and user-centric approach helped us create a product our customers actually want to use.",
    author: "Michael Chen",
    role: "Product Manager",
    company: "FinFlow",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop"
  },
  {
    id: 3,
    quote: "Exceptional designer who truly understands the balance between aesthetics and functionality. Our app went from 3 stars to 4.8 stars after the redesign.",
    author: "Emily Rodriguez",
    role: "Founder",
    company: "HealthMate",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop"
  },
  {
    id: 4,
    quote: "Arslan's design thinking approach helped us identify problems we didn't even know we had. The ROI on their work has been incredible.",
    author: "David Park",
    role: "CTO",
    company: "EduLearn",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop"
  },
  {
    id: 5,
    quote: "A true professional who delivers on time, communicates clearly, and produces work that exceeds expectations. Highly recommended!",
    author: "Lisa Wang",
    role: "Marketing Director",
    company: "ShopEase",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop"
  }
];

export const navigationLinks = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Work", path: "/work" },
  { label: "Services", path: "/services" },
  { label: "Testimonials", path: "/testimonials" },
  { label: "Contact", path: "/contact" },
  { label: "CV", path: "/cv", isCV: true }
];
